import {
  RISK_EXTENSION_GAP_HIERARCHAL_STATUS,
  RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECT,
  RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECTS,
  RISK_EXTENSION_GAP_STATUS_UPDATES,
  RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED,
  RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED_BY,
  RISK_EXTENSION_GAP_STATUS_UPDATE_NOTE,
  RISK_EXTENSION_GAP_STATUS_UPDATE_STATUS,
  RISK_EXTENSION_GAP_STATUS_UPDATE_SUBSTATUSES,
  SYSTEM_ZUS_GAP_SOURCE,
  ZUS_SUPERSEDED_BY_SYSTEM,
} from '@ctw/shared/api/fhir/system-urls';
import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import type { FetchableResource } from '@ctw/shared/context/_internal/resource/resource-fetch';
import { formatFHIRDate, getYearFromISODate } from '@ctw/shared/utils/dates';
import {
  isFetchableResourceType,
  isValidResourceId,
} from '@ctw/shared/utils/fhir/resource-mappings';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import { SYSTEM_URLS } from '@zeus-health/types';
import { formatISO } from 'date-fns';
import type { Coding, MeasureReport } from 'fhir/r4';
import { compact } from 'lodash-es';
import { FHIRModel } from './fhir-model';

const payerMapping = {
  'homeward-payer-data': 'Homeward',
} as const;

export type RiskGapType = 'recapture' | 'suspect' | 'net-new' | 'unknown';
export type RiskGapSource = 'zus-recapture-model' | 'zus-suspect-model' | 'homeward-payer-data';
export type NewGapSource = 'zus-rules' | 'zus-ai' | 'payer-gap' | 'payer-claim' | 'user-update';
export type NewGapType = 'recapture' | 'suspect' | 'net-new';

export const RISK_LAST_ENCOUNTER_DIAGNOSIS_RECORDED_DATE_URL =
  'https://zusapi.com/fhir/StructureDefinition/risk/last-encounter-diagnosis-recorded-date';
export const RISK_GAP_SOURCE_URL = 'https://zusapi.com/fhir/StructureDefinition/risk/gap-source';

export interface RiskGapICD10Code {
  code: string;
  display: string;
  lastEncounterDiagnosisRecordedDate?: string;
  sources: Array<string>;
}

const isPayerSource = (
  source: RiskGapSource | NewGapSource | undefined,
): source is keyof typeof payerMapping => !!source && source in payerMapping;

const getPayerDisplay = (source?: RiskGapSource | NewGapSource): string =>
  payerMapping[source as keyof typeof payerMapping] ?? 'Payer';

export const riskGapStatusMappings: Record<string, LegacyRiskGapStatus> = {
  'open-gap': 'open-gap',
  'accepted-for-review': 'accepted-for-review',
  pending: 'pending',
  dismissed: 'dismissed',
  'expected-to-close': 'expected-to-close',
  'expected-to-close-1st-party': 'expected-to-close-1st-party',
  'expected-to-close-3rd-party': 'expected-to-close-3rd-party',
  'closed-gap': 'closed-unspecified',
  'closed-by-payer-file': 'closed-by-payer-file',
  'closed-by-claims-file': 'closed-by-claims-file',
  'closed-unspecified': 'closed-unspecified',
  closed: 'closed-unspecified',
} as const;

export const riskGapUserStatusOptions = [
  {
    label: 'Open',
    value: 'open-gap',
  },
  {
    label: 'Pending',
    value: 'pending',
  },
  {
    label: 'Accepted for review',
    value: 'accepted-for-review',
  },
  {
    label: 'Dismissed',
    value: 'dismissed',
  },
] as const;

export type RiskGapUserStatus = (typeof riskGapUserStatusOptions)[number]['value'];

export const isRiskGapUserStatus = (status: string): status is RiskGapUserStatus => {
  const isStatus = riskGapUserStatusOptions.some((option) => option.value === status);
  return isStatus;
};

export const isRiskGapStatus = (status: string): status is LegacyRiskGapStatus =>
  riskGapStatusOptions.some((option) => option.value === status);

export const legacyRiskGapSystemStatusOptions = [
  {
    label: 'Expected to close',
    value: 'expected-to-close',
  },
  {
    label: 'Expected to close 1st party',
    value: 'expected-to-close-1st-party',
  },
  {
    label: 'Expected to close 3rd party',
    value: 'expected-to-close-3rd-party',
  },
  {
    label: 'Closed by payer file',
    value: 'closed-gap',
  },
  {
    label: 'Closed by payer file',
    value: 'closed',
  },
  {
    label: 'Closed Unspecified',
    value: 'closed-unspecified',
  },
  {
    label: 'Closed by payer file',
    value: 'closed-by-payer-file',
  },
  {
    label: 'Closed by claims file',
    value: 'closed-by-claims-file',
  },
] as const;

export type RiskGapConfidenceRating = 'low' | 'medium' | 'high';
export const riskGapConfidenceRatings: Array<RiskGapConfidenceRating> = ['low', 'medium', 'high'];

export type RiskGapSystemStatus = (typeof legacyRiskGapSystemStatusOptions)[number]['value'];

const riskGapStatusOptions = [
  ...riskGapUserStatusOptions,
  ...legacyRiskGapSystemStatusOptions,
] as const;
export const riskGapStatuses: NonEmptyArray<LegacyRiskGapStatus> = [
  ...riskGapUserStatusOptions.map((option) => option.value),
  ...legacyRiskGapSystemStatusOptions.map((option) => option.value),
] as NonEmptyArray<LegacyRiskGapStatus>;

export type LegacyRiskGapStatus = RiskGapUserStatus | RiskGapSystemStatus;

export const riskGapDefaultSubstatuses = {
  open: [],
  pending: [
    {
      label: 'Sent to provider for review',
      value: 'pending-sent-to-provider-for-review',
    },
    {
      label: 'Condition being assessed',
      value: 'pending-condition-being-assessed',
    },
    {
      label: 'Other',
      value: 'pending-other',
    },
  ],
  'accepted-for-review': [
    {
      label: 'Sent to provider for review',
      value: 'accepted-for-review-sent-to-provider-for-review',
    },
    {
      label: 'Accepted by provider',
      value: 'accepted-for-review-accepted-by-provider',
    },
    {
      label: 'Condition being assessed',
      value: 'accepted-for-review-condition-being-assessed',
    },
    {
      label: 'Other',
      value: 'accepted-for-review-other',
    },
  ],
  dismissed: [
    {
      label: 'Acute in nature',
      value: 'acute-in-nature',
    },
    {
      label: 'Inaccurate or insufficient evidence',
      value: 'inaccurate-or-insufficient-evidence',
    },
    {
      label: 'Resolved',
      value: 'resolved',
    },
  ],
  'open-gap': [],
  closed: [],
  'closed-gap': [],
  'expected-to-close': [],
  'expected-to-close-1st-party': [],
  'expected-to-close-3rd-party': [],
  'closed-by-payer-file': [],
  'closed-by-claims-file': [],
  'closed-unspecified': [],
} as const;

export interface RiskGapStatusUpdate {
  id: string;
  status: LegacyRiskGapStatus;
  substatuses: Array<string>;
  note: string;
  created: string;
  createdBy: string;
}

export interface Evidence {
  resourceId: string;
  resourceType: string;
  description: string;
  citations: Array<string>;
}

export interface RiskGapEvidenceReference {
  resourceId: string;
  resourceType: string;
  display?: string;
  source: 'evaluatedResource' | 'relatedResourceExtension';
}

export const riskGapThumbsUpRatingReasons = [
  {
    label: 'Highlighted new information',
    value: 'highlighted-new-information',
  },
  {
    label: 'More accurate diagnosis code',
    value: 'more-accurate-diagnosis-code',
  },
  {
    label: 'Other',
    value: 'other',
  },
] as const;

export const riskGapThumbsDownRatingReasons = [
  {
    label: "There's a more appropriate code",
    value: 'theres-a-more-appropriate-code',
  },
  {
    label: 'Additional codes are needed',
    value: 'additional-codes-are-needed',
  },
  {
    label: 'More evidence is needed to confirm this code',
    value: 'more-evidence-is-needed-to-confirm-this-code',
  },
  {
    label: 'Other',
    value: 'other',
  },
] as const;

/** TODO GAP-1163: use RiskGapMeasureReport from @zeus-health/types */
export class RiskGapModel extends FHIRModel<MeasureReport> {
  public kind = 'MeasureReport' as const;

  public get resourceType(): ResourceTypeString {
    return 'MeasureReport';
  }

  public get supersededByHccCodes(): Array<Coding> {
    const hierarchicalStatusExtension = this.resource?.group?.[0]?.extension?.find(
      (ext) => ext.url === RISK_EXTENSION_GAP_HIERARCHAL_STATUS,
    );
    const hierarchicalStatusCoding = hierarchicalStatusExtension?.valueCodeableConcept?.coding?.[0];
    const supersededByExtension = hierarchicalStatusCoding?.extension?.find(
      (ext) => ext.url === ZUS_SUPERSEDED_BY_SYSTEM,
    );
    const codes = supersededByExtension?.valueCodeableConcept?.coding;
    return codes?.filter((hcc) => hcc.system === HCC_CODE_SYSTEM_URL) ?? [];
  }

  public get newSupersededByHccCodes(): Array<Coding> {
    const supersededByExtension = this.resource?.extension?.find(
      (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_SUPERSEDED_BY,
    );
    const codes = supersededByExtension?.valueCodeableConcept?.coding;
    return codes?.filter((hcc) => hcc.system === HCC_CODE_SYSTEM_URL) ?? [];
  }

  public get newSupersedingHccCodes(): Array<Coding> {
    const supersedingExtension = this.resource?.extension?.find(
      (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_SUPERSEDING,
    );
    const codes = supersedingExtension?.valueCodeableConcept?.coding;
    return codes?.filter((hcc) => hcc.system === HCC_CODE_SYSTEM_URL) ?? [];
  }

  public get hierarchicalStatus(): string {
    const hierarchicalStatus = this.resource?.group?.[0]?.extension?.find(
      (ext) => ext.url === RISK_EXTENSION_GAP_HIERARCHAL_STATUS,
    )?.valueCodeableConcept?.coding?.[0]?.code;
    return hierarchicalStatus ?? 'applied-not-superseded';
  }

  public get evidence(): Array<Evidence> {
    if (!this.resource?.evaluatedResource?.[0]) {
      return [];
    }

    return this.resource.evaluatedResource.map((evidence) => {
      const [resourceType, resourceId] = (evidence.reference ?? '').split('/');
      return {
        resourceId,
        resourceType,
        description: evidence.display || this.title,
        citations: [],
      };
    });
  }

  public get evidenceReferences(): Array<RiskGapEvidenceReference> {
    const referencesByKey = new Map<string, RiskGapEvidenceReference>();

    const evaluatedResources = this.resource?.evaluatedResource ?? [];
    for (const evaluatedResource of evaluatedResources) {
      const [resourceType, resourceId] = (evaluatedResource.reference ?? '').split('/');
      if (!(resourceType && resourceId)) {
        continue;
      }

      const reference: RiskGapEvidenceReference = {
        resourceId,
        resourceType,
        display: evaluatedResource.display,
        source: 'evaluatedResource',
      };
      referencesByKey.set(`${resourceType}|${resourceId}`, reference);
    }

    const relatedRecordsExtension = this.resource.extension?.find(
      (extension) =>
        extension.url === RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECTS ||
        extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_RELATED_CLINICAL_OBJECTS,
    );

    const relatedRecordExtensions = relatedRecordsExtension?.extension ?? [];
    for (const relatedRecordExtension of relatedRecordExtensions) {
      if (
        relatedRecordExtension.url !== RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECT &&
        relatedRecordExtension.url !== SYSTEM_URLS.EXTENSIONS.RISK_GAP_RELATED_CLINICAL_OBJECT
      ) {
        continue;
      }

      const [resourceType, resourceId] = relatedRecordExtension.valueReference?.reference?.split(
        '/',
      ) ?? ['', ''];
      if (!(resourceType && resourceId)) {
        continue;
      }

      const existingReference = referencesByKey.get(`${resourceType}|${resourceId}`);
      if (existingReference) {
        if (!existingReference.display && relatedRecordExtension.valueReference?.display) {
          referencesByKey.set(`${resourceType}|${resourceId}`, {
            ...existingReference,
            display: relatedRecordExtension.valueReference.display,
          });
        }
        continue;
      }

      referencesByKey.set(`${resourceType}|${resourceId}`, {
        resourceId,
        resourceType,
        display: relatedRecordExtension.valueReference?.display,
        source: 'relatedResourceExtension',
      });
    }

    return Array.from(referencesByKey.values());
  }

  public get relatedRecords(): Array<FetchableResource> {
    const relatedRecordsExtension = this.resource.extension?.find(
      (extension) =>
        extension.url === RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECTS ||
        extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_RELATED_CLINICAL_OBJECTS,
    );

    return (
      compact(
        relatedRecordsExtension?.extension?.map((relatedRecordExtension) => {
          if (
            relatedRecordExtension.url !== RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECT &&
            relatedRecordExtension.url !== SYSTEM_URLS.EXTENSIONS.RISK_GAP_RELATED_CLINICAL_OBJECT
          ) {
            return null;
          }

          const [resourceType, resourceId] =
            relatedRecordExtension.valueReference?.reference?.split('/') ?? ['', ''];
          if (!(isFetchableResourceType(resourceType) && isValidResourceId(resourceId))) {
            return null;
          }

          return {
            resourceType,
            resourceId,
          };
        }),
      ) ?? []
    );
  }

  public get statusUpdates(): Array<RiskGapStatusUpdate> {
    const statusUpdatesExtension = this.resource.extension?.find(
      (extension) =>
        extension.url === RISK_EXTENSION_GAP_STATUS_UPDATES ||
        extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_STATUS_UPDATES,
    );

    const updates =
      statusUpdatesExtension?.extension
        ?.map((statusUpdateExtension, index): RiskGapStatusUpdate | null => {
          const legacyUpdatesStatus =
            statusUpdateExtension.extension?.find(
              (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_STATUS,
            )?.valueCodeableConcept?.coding?.[0]?.code ?? '';

          const newUpdatesStatus =
            statusUpdateExtension.extension?.find(
              (extension) => extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_STATUS_UPDATE_STATUS,
            )?.valueString ?? '';

          const statusValue = legacyUpdatesStatus || newUpdatesStatus;
          if (!statusValue) {
            return null;
          }

          if (legacyUpdatesStatus) {
            const riskGapStatus = isRiskGapStatus(legacyUpdatesStatus);
            return {
              id: `${this.id}|${index}`,
              status: riskGapStatus
                ? legacyUpdatesStatus
                : (riskGapStatusMappings[legacyUpdatesStatus] ?? 'open-gap'),
              substatuses:
                statusUpdateExtension.extension
                  ?.find(
                    (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_SUBSTATUSES,
                  )
                  ?.valueCodeableConcept?.coding?.map((coding) => coding.code)
                  ?.filter((c): c is string => !!c) ?? [],
              note:
                statusUpdateExtension.extension?.find(
                  (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_NOTE,
                )?.valueString ?? '',
              created: (() => {
                const createdExtension = statusUpdateExtension.extension?.find(
                  (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED,
                );
                return createdExtension?.valueDateTime ?? createdExtension?.valueDate ?? '';
              })(),
              createdBy:
                statusUpdateExtension.extension?.find(
                  (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED_BY,
                )?.valueString ?? '',
            };
          }

          if (newUpdatesStatus) {
            return {
              id: `${this.id}|${index}`,
              status: riskGapStatusMappings[newUpdatesStatus] ?? 'open-gap',
              substatuses:
                statusUpdateExtension.extension
                  ?.find(
                    (extension) =>
                      extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_STATUS_UPDATE_SUBSTATUSES,
                  )
                  ?.valueCodeableConcept?.coding?.map((coding) => coding.code)
                  ?.filter((c): c is string => !!c) ?? [],
              note:
                statusUpdateExtension.extension?.find(
                  (extension) =>
                    extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_STATUS_UPDATE_NOTE,
                )?.valueString ?? '',
              created: (() => {
                const createdExtension = statusUpdateExtension.extension?.find(
                  (extension) =>
                    extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_STATUS_UPDATE_CREATED,
                );
                return createdExtension?.valueDateTime ?? createdExtension?.valueDate ?? '';
              })(),
              createdBy:
                statusUpdateExtension.extension?.find(
                  (extension) =>
                    extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_STATUS_UPDATE_CREATED_BY,
                )?.valueString ?? '',
            };
          }

          return null;
        })
        .filter((u): u is RiskGapStatusUpdate => u !== null) ?? [];

    return updates.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  }

  public get lastStatusUpdate(): RiskGapStatusUpdate {
    const lastUpdate = this.statusUpdates.at(-1);

    // Exclude status updates with `status` values we don't recognize because we don't know how to display those.
    if (!(lastUpdate && isRiskGapStatus(lastUpdate.status))) {
      return {
        id: `${this.id}|`,
        status: 'open-gap',
        substatuses: [],
        note: '',
        created: this.resource.date ?? this.createdAt ?? formatISO(new Date()),
        createdBy: isPayerSource(this.gapSource)
          ? `${getPayerDisplay(this.gapSource)} Gap File`
          : 'Zus Automation',
      };
    }

    return lastUpdate;
  }

  public get lastStatusUpdateDate(): string {
    return this.lastStatusUpdate.created;
  }

  public get lastUpdatedByDisplay(): string {
    // A URL in this field indicates a status update made by a Zus system
    if (
      this.lastStatusUpdate.createdBy.startsWith('http') ||
      this.lastStatusUpdate.createdBy.startsWith('zus-')
    ) {
      return 'Zus';
    }

    return this.lastStatusUpdate.createdBy;
  }

  public get icd10Codes(): Array<RiskGapICD10Code> {
    const codes = this.resource?.group?.[0]?.code?.coding;
    if (!codes) {
      return [];
    }
    return codes
      .filter((icd10) => icd10.system === ICD10_CODE_SYSTEM || icd10.system === ICD10CM_CODE_SYSTEM)
      .map((icd10) => {
        const extensions =
          (
            icd10 as {
              extension?: Array<{ url: string; valueDateTime?: string; valueString?: string }>;
            }
          ).extension ?? [];
        const lastEncounterDiagnosisRecordedDate = extensions.find(
          (ext) => ext.url === RISK_LAST_ENCOUNTER_DIAGNOSIS_RECORDED_DATE_URL,
        )?.valueDateTime;
        const sources = extensions
          .filter((ext) => ext.url === RISK_GAP_SOURCE_URL)
          .map((ext) => ext.valueString)
          .filter((s): s is string => !!s);

        return {
          code: icd10.code ?? '',
          display: icd10.display ?? '',
          lastEncounterDiagnosisRecordedDate,
          sources,
        };
      });
  }

  public get hccCode(): string {
    return (
      this.resource.group?.[0]?.code?.coding?.find(
        (coding) => coding.system === HCC_CODE_SYSTEM_URL,
      )?.code ?? 'N/A'
    );
  }

  public get title(): string {
    return this.resource.group?.[0]?.code?.coding?.[0]?.display ?? '';
  }

  public get type(): RiskGapType | NewGapType {
    const reportGroupExtensions = this.resource?.group?.[0]?.extension ?? this.resource.extension;

    const suspectTypeExtension = reportGroupExtensions?.find(
      (ext) => ext.url === RA_SUSPECT_TYPE_URL || ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_TYPE,
    );

    const type =
      suspectTypeExtension?.valueCodeableConcept?.coding?.[0]?.code ??
      suspectTypeExtension?.valueString;

    switch (type) {
      case 'recapture':
      case 'historic':
        return 'recapture';
      case 'suspect':
      case 'suspected':
        return 'suspect';
      case 'net-new':
        return 'net-new';
      default:
        return 'unknown';
    }
  }

  // Consider this function to be deprecated, given that a risk gap now has a list of gap sources
  // rather than a single source.  To maintain backwards compatbility (for now) we'll return
  // a source from the list using the some prioritization which was orginally done in SQL.
  public get gapSource(): RiskGapSource | undefined {
    const gapSourceList = this.gapSourceList ?? this.gapSourceFromNewBackend;
    if (gapSourceList.includes('homeward-payer-data') || gapSourceList.includes('payer-gap')) {
      return 'homeward-payer-data';
    }
    if (gapSourceList.includes('zus-suspect-model') || gapSourceList.includes('zus-ai')) {
      return 'zus-suspect-model';
    }
    if (gapSourceList.includes('zus-recapture-model') || gapSourceList.includes('zus-rules')) {
      return 'zus-recapture-model';
    }

    return undefined;
  }

  public get gapSourceList(): Array<RiskGapSource | NewGapSource> {
    const reportExtensions = this.resource.extension ?? [];

    const legacySources = reportExtensions
      .filter((ext) => ext.url === GAP_SOURCE_URL)
      .map(
        (ext) =>
          ext.valueCodeableConcept?.coding?.find((c) => c.system === SYSTEM_ZUS_GAP_SOURCE)?.code,
      )
      .filter((code): code is RiskGapSource => !!code);

    const newBackendSources = this.gapSourceFromNewBackend.filter(
      (code): code is NewGapSource => !!code,
    );
    const displayList = Array.from(
      new Set<RiskGapSource | NewGapSource>([...legacySources, ...newBackendSources]),
    );

    // This will all be removed later, for now we exclude user-update as a possible source
    return displayList.filter((item) => item !== 'user-update');
  }

  public get gapSourceListDisplay(): string {
    return [...new Set(this.gapSourceList.map(this.gapSourceToString))]
      .sort()
      .filter(Boolean)
      .join(', ');
  }

  private readonly gapSourceToString = (
    source: RiskGapSource | NewGapSource | undefined,
  ): string => {
    switch (source) {
      case 'zus-rules':
      case 'zus-ai':
      case 'zus-suspect-model':
      case 'zus-recapture-model':
        return 'Zus';
      case 'payer-gap':
      case 'homeward-payer-data':
        return getPayerDisplay(source);
      default:
        return '';
    }
  };

  public get gapSourceFromNewBackend(): Array<string> {
    const statusUpdatesExtension = this.resource?.extension?.find(
      (extension) => extension.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_STATUS_UPDATES,
    );

    const statusUpdates = statusUpdatesExtension?.extension ?? [];
    if (statusUpdates.length === 0) {
      return [];
    }

    const sources = statusUpdates
      .flatMap((statusUpdate) => statusUpdate.extension ?? [])
      .filter((e) => e.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_STATUS_UPDATE_SOURCE)
      .map((e) => e.valueString)
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);

    return Array.from(new Set(sources));
  }

  public get rafScore(): number | null {
    return this.resource?.group?.[0]?.measureScore?.value ?? null;
  }

  public get status(): LegacyRiskGapStatus {
    return this.lastStatusUpdate.status ?? 'open';
  }

  public get statusDisplay(): string {
    if (this.status === 'pending') {
      return `Pending ${formatFHIRDate(this.lastStatusUpdateDate)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status === 'accepted-for-review') {
      return `Accepted for review ${formatFHIRDate(this.lastStatusUpdateDate)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status === 'dismissed') {
      return `Dismissed ${formatFHIRDate(this.lastStatusUpdateDate)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status.includes('expected-to-close')) {
      // To account for new 1st and 3rd party statuses
      return 'Expected to Close by Encounter Diagnosis';
    }

    if (this.status === 'closed-by-payer-file') {
      return `Closed ${formatFHIRDate(this.lastStatusUpdateDate)} by Payer file`;
    }

    if (this.status === 'closed-gap') {
      return 'Closed';
    }

    if (this.status === 'closed-unspecified') {
      return 'Closed';
    }

    if (this.status === 'closed-by-claims-file') {
      return `Closed ${formatFHIRDate(this.lastStatusUpdateDate)} by Claims file`;
    }

    return `Opened ${formatFHIRDate(this.lastStatusUpdateDate)} by ${this.lastUpdatedByDisplay}`;
  }

  public get substatuses(): Array<string> {
    return this.lastStatusUpdate.substatuses ?? [];
  }

  public get suspectedDisplay(): string | undefined {
    // SuspectedDate only applies to suspect risk gaps, where it is simply the date field on the
    // MeasureReport.
    if (this.type !== 'suspect') {
      return undefined;
    }
    return this.resource.date ? formatFHIRDate(this.resource.date) : undefined;
  }

  public get lastDiagnosedDisplay(): string | undefined {
    const evidenceStatusDateExtension = this.resource?.group?.[0]?.extension?.find(
      (ext) => ext.url === RA_EVIDENCE_STATUS_DATE_URL,
    )?.valueDate;
    return evidenceStatusDateExtension ? formatFHIRDate(evidenceStatusDateExtension) : undefined;
  }

  public get confidenceLevel(): number | null {
    if (this.type === 'suspect') {
      const reportGroupExtensions = this.resource?.group?.[0]?.extension;
      const legacyConfidenceExtension = reportGroupExtensions?.find(
        (ext) => ext.url === RA_CONFIDENCE_LEVEL_URL,
      );
      const newConfidenceExtension = this.resource.extension?.find(
        (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.RISK_GAP_CONFIDENCE_LEVEL,
      );
      return (
        legacyConfidenceExtension?.valueInteger ?? newConfidenceExtension?.valueInteger ?? null
      );
    }
    // Return max confidence level for recapture gaps (or any other as)
    if (this.type === 'recapture') {
      return 5;
    }
    if (this.type === 'net-new') {
      return 5;
    }
    return null;
  }

  public get confidenceRating(): RiskGapConfidenceRating | undefined {
    switch (this.confidenceLevel) {
      case 1:
      case 2:
      case 3:
        return 'low';
      case 4:
        return 'medium';
      case 5:
        return 'high';
      default:
        return undefined;
    }
  }

  public get period(): string | undefined {
    if (!this.resource.period?.start) {
      return undefined;
    }

    return getYearFromISODate(this.resource.period.start);
  }
}

export const RISK_MEASURE_URL = 'http://zusapi.com/quality/measure/risk/CMSv28';

// Constants for FHIR extension URLs
const RA_SUSPECT_TYPE_URL = 'http://hl7.org/fhir/us/davinci-ra/StructureDefinition/ra-suspectType';
const RA_EVIDENCE_STATUS_DATE_URL =
  'http://hl7.org/fhir/us/davinci-ra/StructureDefinition/ra-evidenceStatusDate';
const RA_CONFIDENCE_LEVEL_URL =
  'http://hl7.org/fhir/us/davinci-ra/StructureDefinition/ra-confidenceLevel';
export const GAP_SOURCE_URL = 'https://zusapi.com/fhir/identifier/quality/riskgap/gap-source';
const HCC_CODE_SYSTEM_URL = 'http://terminology.hl7.org/CodeSystem/cmshcc' as const;
const ICD10_CODE_SYSTEM = 'http://hl7.org/fhir/sid/icd-10' as const;
const ICD10CM_CODE_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm' as const;
